import { useForm, useStore } from "@tanstack/react-form";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { TextLink } from "@/components/text";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { UpgradePlanCard } from "@/ui/upgrade-plan-card";
import type { cursosImportarClub_club$key } from "./__generated__/cursosImportarClub_club.graphql";
import type { cursosImportarClubQuery } from "./__generated__/cursosImportarClubQuery.graphql";
import type { cursosImportarHotmartMutation } from "./__generated__/cursosImportarHotmartMutation.graphql";
import type { cursosImportarKiwifyMutation } from "./__generated__/cursosImportarKiwifyMutation.graphql";
import type { cursosImportarMemberKitMutation } from "./__generated__/cursosImportarMemberKitMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$category/importar",
)({
	component: RouteComponent,
});

type CourseStatus = "" | "none" | "hotmart" | "memberkit" | "kiwify" | "other";

function useClubFeatures(clubFragment: cursosImportarClub_club$key) {
	const clubData = useFragment(
		graphql`
			fragment cursosImportarClub_club on Club {
				subscription {
					features {
						key
						value
					}
				}
				...upgradePlanCard_clubFragment
			}
		`,
		clubFragment,
	);

	const hasCommentImportFeature =
		clubData.subscription?.features?.find(
			(feat) => feat.key === "comment_import",
		)?.value === "1";

	return { clubData, hasCommentImportFeature };
}

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { club: clubSlug, category } = useParams({ from: Route.id });

	const queryData = useLazyLoadQuery<cursosImportarClubQuery>(
		graphql`
			query cursosImportarClubQuery($clubSlug: String!) {
				club(slug: $clubSlug) {
					...cursosImportarClub_club
				}
			}
		`,
		{ clubSlug },
	);

	const { clubData, hasCommentImportFeature } = useClubFeatures(queryData.club);

	const [importHotmartCourse, isHotmartPending] =
		useMutation<cursosImportarHotmartMutation>(
			graphql`
				mutation cursosImportarHotmartMutation($input: HotmartCourseImportInput!) {
					hotmartCourseImport(input: $input) {
						id
					}
				}
			`,
		);

	const [importKiwifyCourse, isKiwifyPending] =
		useMutation<cursosImportarKiwifyMutation>(
			graphql`
				mutation cursosImportarKiwifyMutation($input: KiwifyCourseImportInput!) {
					kiwifyCourseImport(input: $input) {
						id
					}
				}
			`,
		);

	const [importMemberKitCourse, isMemberKitPending] =
		useMutation<cursosImportarMemberKitMutation>(
			graphql`
				mutation cursosImportarMemberKitMutation($club: String!, $input: MemberKitCourseImportInput!) {
					memberKitCourseImport(club: $club, input: $input) {
						id
					}
				}
			`,
		);

	const isMutationPending =
		isHotmartPending || isKiwifyPending || isMemberKitPending;

	const form = useForm({
		defaultValues: {
			courseStatus: "" as CourseStatus,
			targetUrl: "",
			username: "",
			password: "",
			courseId: "",
			token: "",
			deviceId: "",
			courseName: "",
			importComments: false,
		},
		onSubmit: async ({ value }) => {
			return new Promise((resolve, reject) => {
				if (value.courseStatus === "hotmart") {
					importHotmartCourse({
						variables: {
							input: {
								category,
								club: clubSlug,
								token: value.token,
								courseId: parseInt(value.courseId, 10),
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							toast.success("Importação iniciada com sucesso!");
							navigate({
								to: "/c/$club/area-de-membros/cursos",
								params: { club: clubSlug },
							});
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else if (value.courseStatus === "kiwify") {
					importKiwifyCourse({
						variables: {
							input: {
								category,
								club: clubSlug,
								token: value.token,
								deviceId: value.deviceId,
								courseName: value.courseName,
								importComments: value.importComments,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							toast.success("Importação iniciada com sucesso!");
							navigate({
								to: "/c/$club/area-de-membros/cursos",
								params: { club: clubSlug },
							});
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else if (value.courseStatus === "memberkit") {
					importMemberKitCourse({
						variables: {
							club: clubSlug,
							input: {
								category,
								loginUrl: value.targetUrl,
								targetUrl: value.targetUrl,
								username: value.username,
								password: value.password,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							toast.success("Importação iniciada com sucesso!");
							navigate({
								to: "/c/$club/area-de-membros/cursos",
								params: { club: clubSlug },
							});
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao importar curso. Tente novamente.");
							reject(error);
						},
					});
				} else {
					reject(new Error("Plataforma não selecionada"));
				}
			});
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const disableFields = isSubmitting || isMutationPending;

	return (
		<div className="p-6">
			<form
				className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<CardTitle>Importar Curso</CardTitle>

				<div className="flex flex-col gap-4">
					<form.Field
						name="courseStatus"
						validators={{
							onChange: ({ value }) =>
								!value ? { message: "Selecione uma plataforma" } : undefined,
						}}
					>
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>
									Plataforma do curso
								</FieldLabel>
								<Select
									name={field.name}
									value={field.state.value}
									disabled={disableFields}
									onValueChange={(e) => {
										field.handleChange(e as CourseStatus);
									}}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Selecione a plataforma" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="hotmart">Hotmart</SelectItem>
										<SelectItem value="memberkit">Memberkit</SelectItem>
										<SelectItem value="kiwify">Kiwify</SelectItem>
										<SelectItem value="other">
											Outra plataforma (não listada)
										</SelectItem>
									</SelectContent>
								</Select>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>
				</div>

				<form.Subscribe selector={(state) => [state.values.courseStatus]}>
					{([courseStatus]) => (
						<>
							{courseStatus === "hotmart" && (
								<Alert>
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>Para importar da Hotmart, siga nosso guia:</span>
											<TextLink
												href="https://ajuda.scarf.club/docs/cursos/migrando-da-hotmart/"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Como migrar da Hotmart
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "kiwify" && (
								<Alert>
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>Para importar da Kiwify, siga nosso guia:</span>
											<TextLink
												href="https://ajuda.scarf.club/docs/cursos/migrando-da-kiwify/"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Como migrar da Kiwify
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "other" && (
								<Alert>
									<AlertDescription>
										<div className="flex flex-col gap-2">
											<span>
												Infelizmente ainda não temos importação automática dessa
												plataforma, mas entre em contato para que possamos
												ajudar na migração.
											</span>
											<TextLink
												href="https://wa.me/5521987293362?text=Estou%20criando%20meu%20ecossistema%20scarf%20e%20tenho%20curso%20na%20plataforma"
												target="_blank"
												rel="noopener noreferrer"
												className="flex flex-row items-center justify-start gap-2"
											>
												Falar no WhatsApp
												<Icon name="MoveUpRight" className="size-3" />
											</TextLink>
										</div>
									</AlertDescription>
								</Alert>
							)}

							{courseStatus === "memberkit" && (
								<div className="flex flex-col gap-4">
									<form.Field
										name="targetUrl"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "URL do Curso é obrigatória" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													URL do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="https://memberkit.com.br/seu-curso"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="username"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Usuário é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>Usuário</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu.usuario"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="password"
										validators={{
											onChange: ({ value }) =>
												!value ? { message: "Senha é obrigatória" } : undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>Senha</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													type="password"
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="•••••••"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								</div>
							)}

							{courseStatus === "hotmart" && (
								<div className="flex flex-col gap-4">
									<form.Field
										name="token"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Token de autenticação é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Token de autenticação
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													disabled={disableFields}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-token"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="courseId"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "ID do Curso é obrigatório" }
													: !/^\d+$/.test(value)
														? {
																message:
																	"ID do Curso deve ser um número válido",
															}
														: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													ID do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="123456"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								</div>
							)}

							{courseStatus === "kiwify" && (
								<div className="flex flex-col gap-4">
									<form.Field
										name="token"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Token de autenticação é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Token de autenticação
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													disabled={disableFields}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-token"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="deviceId"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "ID do Dispositivo é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													ID do Dispositivo
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="seu-device-id"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field
										name="courseName"
										validators={{
											onChange: ({ value }) =>
												!value
													? { message: "Nome do Curso é obrigatório" }
													: undefined,
										}}
									>
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Nome do Curso
												</FieldLabel>
												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="Nome do curso"
													disabled={disableFields}
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>
												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<form.Field name="importComments">
										{(field) => (
											<Field orientation="horizontal">
												<FieldLabel htmlFor={field.name}>
													Importar comentários
												</FieldLabel>
												{hasCommentImportFeature ? (
													<Switch
														id={field.name}
														checked={field.state.value}
														onCheckedChange={(checked) =>
															field.handleChange(checked === true)
														}
														disabled={disableFields}
													/>
												) : (
													<Switch id={field.name} checked={false} disabled />
												)}
											</Field>
										)}
									</form.Field>

									{!hasCommentImportFeature && (
										<UpgradePlanCard club={clubData} />
									)}
								</div>
							)}
						</>
					)}
				</form.Subscribe>

				{isSubmitting && (
					<Alert className="bg-primary/5 border-primary!">
						<AlertDescription className="flex items-center gap-2">
							<Spinner />
							Estamos validando suas credenciais...
						</AlertDescription>
					</Alert>
				)}

				<Separator />

				<form.Subscribe selector={(state) => state.values.courseStatus}>
					{(courseStatus) => (
						<div className="flex items-center justify-end gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={() =>
									navigate({
										to: "/c/$club/area-de-membros/cursos",
										params: { club: clubSlug },
									})
								}
								disabled={isSubmitting}
							>
								Cancelar
							</Button>

							<Button
								type="submit"
								className="flex items-center gap-2"
								disabled={
									disableFields ||
									!courseStatus ||
									courseStatus === "other" ||
									courseStatus === "none"
								}
							>
								Importar
								{isSubmitting && <Spinner />}
							</Button>
						</div>
					)}
				</form.Subscribe>
			</form>
		</div>
	);
}
